import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Chip,
  Stack,
  Select,
  Dialog,
  MenuItem,
  Checkbox,
  TextField,
  InputLabel,
  Typography,
  IconButton,
  FormControl,
  DialogTitle,
  Autocomplete,
  ListItemText,
  DialogContent,
} from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { phoneNumberSplit } from 'src/utils/phoneNumberSplit';

import { fetchRoles } from 'src/services/role.service';
import { fetchDepartments } from 'src/services/department.service';

import Iconify from 'src/components/iconify';

import { scrollbarStyle } from 'src/sections/style';

import countries from '../../../locale/phoneCode';
import { EMAIL_REGEX, PHONENUMBER_REGEX } from '../../../utils/regex';

const SELECT_ALL_VALUE = 'SELECT_ALL';

ModifyUserModal.propTypes = {
  row: PropTypes.object,
  setOpen: PropTypes.func,
  modifyUser: PropTypes.func,
  open: PropTypes.bool,
  currentRole: PropTypes.string,
  departmentsData: PropTypes.array,
};

export default function ModifyUserModal({
  row,
  setOpen,
  modifyUser,
  open,
  currentRole,
  departmentsData,
}) {
  const { i18n, t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const [rolesList, setRolesList] = useState([]);
  const [localDepartments, setLocalDepartments] = useState([]); // Stores freshly fetched departments
  const { permissions } = useAuth();

  // Automatically fetches the absolute freshest department list the second this modal opens
  useEffect(() => {
    if (open) {
      const loadFreshDepts = async () => {
        try {
          const data = await fetchDepartments();
          setLocalDepartments(data || []);
        } catch (err) {
          console.error('Failed to fetch departments on modal open:', err);
        }
      };
      loadFreshDepts();
    }
  }, [open]);
  const canUpdateRole =
    permissions?.includes('ROLES:CREATE') || permissions?.includes('ROLES:UPDATE');
  const canEditDept = permissions?.includes('DEPARTMENT:UPDATE');

  useEffect(() => {
    const getRoles = async () => {
      try {
        const data = await fetchRoles();
        const filteredRoles = data.filter((role) => {
          const isActive = role.active !== undefined ? role.active : role.isActive;
          return isActive || role.roleName === row?.role;
        });
        setRolesList(filteredRoles);
      } catch (error) {
        console.error('Failed to fetch roles:', error);
      }
    };
    getRoles();
    countries.sort((a, b) => a[i18n.language].localeCompare(b[i18n.language]));
  }, [i18n.language, row?.role]);

  const normalizeData = (data) => {
    const split = phoneNumberSplit(data?.phoneNumber || '');

    const deptIds =
      (data?.departmentIds &&
      Array.isArray(data.departmentIds) &&
      data.departmentIds.length > 0
        ? data.departmentIds.map(Number)
        : null) ||
      (data?.uniqueDepartmentIds &&
      Array.isArray(data.uniqueDepartmentIds) &&
      data.uniqueDepartmentIds.length > 0
        ? data.uniqueDepartmentIds.map(Number)
        : null) ||
      (data?.departments && Array.isArray(data.departments)
        ? data.departments.map((d) => Number(typeof d === 'object' ? d.id : d))
        : []);

    return {
      id: data?.id || '',
      givenName: data?.givenName || '',
      familyName: data?.familyName || '',
      countryCode: split.partOne || '+39',
      phoneNumber: split.partTwo || '',
      role: data?.role || '',
      departmentIds: deptIds,
      email: data?.email || '',
    };
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
    reset,
  } = useForm({
    mode: 'all',
    defaultValues: normalizeData(row),
  });

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const cleanRoleName = (roleName) => {
    if (!roleName) return '';
    return roleName
      .replace('ROLE_', '')
      .replace(/_/g, ' ')
      .trim()
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const onSubmit = (data) => {
    data.phoneNumber = `(${data.countryCode}) ${data.phoneNumber}`;
    modifyUser(data);
    handleClose();
  };

  useEffect(() => {
    if (row) {
      reset(normalizeData(row));
    }
  }, [row, reset]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle id="modal--title">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontSize: isSmallScreen ? '0.85rem' : '1rem' }}>
            {t('pages.usersPage.form.modifyUser')}
          </Typography>
          <IconButton onClick={handleClose} sx={{ mr: -1.5 }}>
            <Iconify
              sx={{ color: 'text.secondary', '&:hover': { color: '#B72136' } }}
              width={isSmallScreen ? 25 : 30}
              icon="eva:close-square-fill"
            />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3} mt={2}>
            <TextField
              name="givenName"
              label={t('attributes.name')}
              error={!!errors.givenName}
              helperText={errors.givenName?.message || ''}
              {...register('givenName', {
                required: t('formControl.enterName'),
                minLength: { value: 3, message: t('formControl.minName') },
              })}
              size={isSmallScreen ? 'small' : 'medium'}
            />
            <TextField
              name="familyName"
              label={t('attributes.lastName')}
              error={!!errors.familyName}
              helperText={errors.familyName?.message || ''}
              {...register('familyName', {
                required: t('formControl.enterLasName'),
                minLength: { value: 3, message: t('formControl.minLastName') },
              })}
              size={isSmallScreen ? 'small' : 'medium'}
            />

            <Stack direction="row" spacing={1}>
              <FormControl sx={{ width: '30%' }}>
                <InputLabel id="country-code-label">{t('attributes.country')}</InputLabel>
                <Controller
                  name="countryCode"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="country-code-label"
                      label={t('attributes.country')}
                      renderValue={(val) => `(${val})`}
                      size={isSmallScreen ? 'small' : 'medium'}
                    >
                      {countries.map((country, index) => (
                        <MenuItem key={index} value={country.code}>{`${
                          country[i18n.language]
                        } (${country.code})`}</MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>

              <TextField
                fullWidth
                name="phoneNumber"
                type="number"
                label={t('attributes.phoneNumber')}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber?.message || ''}
                {...register('phoneNumber', {
                  required: t('formControl.enterPhoneNumber'),
                  pattern: {
                    value: PHONENUMBER_REGEX,
                    message: t('formControl.invalidPhoneNumber'),
                  },
                })}
                size={isSmallScreen ? 'small' : 'medium'}
              />
            </Stack>

            {canUpdateRole && (
              <Controller
                name="role"
                control={control}
                rules={{ required: t('formControl.selectRole') }}
                render={({ field }) => (
                  <Autocomplete
                    fullWidth
                    options={rolesList}
                    value={rolesList.find((r) => r.roleName === field.value) || null}
                    getOptionLabel={(option) => cleanRoleName(option?.roleName || '')}
                    isOptionEqualToValue={(option, value) =>
                      option.roleName === value.roleName
                    }
                    onChange={(_, newValue) =>
                      field.onChange(newValue ? newValue.roleName : '')
                    }
                    onBlur={field.onBlur}
                    size={isSmallScreen ? 'small' : 'medium'}
                    noOptionsText={t('formControl.noRoleFound', 'No matching role')}
                    ListboxProps={{ sx: scrollbarStyle }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('pages.profilePage.role')}
                        error={!!errors.role}
                        helperText={errors.role?.message}
                      />
                    )}
                  />
                )}
              />
            )}

            {canEditDept && (
              <Controller
                name="departmentIds"
                control={control}
                rules={{ validate: (v) => (v && v.length > 0) || 'Membership required' }}
                render={({ field }) => {
                  // Uses the dynamically loaded local list first, falling back to props
                  const safeDepts =
                    localDepartments.length > 0
                      ? localDepartments
                      : departmentsData || [];
                  const currentValue = (field.value || []).map(Number);
                  const isAllSelected =
                    safeDepts.length > 0 && currentValue.length === safeDepts.length;
                  const selectedOptions = safeDepts.filter((d) =>
                    currentValue.includes(Number(d.id))
                  );
                  const options = [
                    {
                      id: SELECT_ALL_VALUE,
                      name: t('buttons.selectAllDepartments', 'Select All Departments'),
                    },
                    ...safeDepts,
                  ];

                  return (
                    <Autocomplete
                      fullWidth
                      multiple
                      disableCloseOnSelect
                      options={options}
                      value={selectedOptions}
                      getOptionLabel={(option) => option.name || ''}
                      isOptionEqualToValue={(option, value) =>
                        Number(option.id) === Number(value.id)
                      }
                      filterOptions={(opts, state) => {
                        const input = state.inputValue.toLowerCase();
                        return opts.filter(
                          (o) =>
                            o.id === SELECT_ALL_VALUE ||
                            o.name.toLowerCase().includes(input)
                        );
                      }}
                      onChange={(event, newValue) => {
                        const clickedSelectAll = newValue.some(
                          (o) => o.id === SELECT_ALL_VALUE
                        );
                        if (clickedSelectAll) {
                          field.onChange(
                            isAllSelected ? [] : safeDepts.map((d) => Number(d.id))
                          );
                          return;
                        }
                        field.onChange(newValue.map((o) => Number(o.id)));
                      }}
                      componentsProps={{
                        paper: {
                          sx: {
                            mt: 0.5,
                            borderRadius: 2,
                            boxShadow: (theme) =>
                              theme.customShadows?.z20 || '0 8px 24px rgba(0,0,0,0.14)',
                          },
                        },
                      }}
                      renderOption={(props, option, { selected }) => {
                        const optionProps = props;
                        const isSelectAll = option.id === SELECT_ALL_VALUE;
                        const checked = isSelectAll ? isAllSelected : selected;
                        const indeterminate =
                          isSelectAll &&
                          currentValue.length > 0 &&
                          currentValue.length < safeDepts.length;

                        return (
                          <Box
                            component="li"
                            {...optionProps}
                            sx={{
                              px: 1.5,
                              py: 1,
                              borderRadius: 1,
                              mx: 0.5,
                              my: isSelectAll ? 0.5 : 0,
                              ...(isSelectAll && {
                                bgcolor: (theme) =>
                                  alpha(theme.palette.primary.main, 0.08),
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                              }),
                              '&:hover': {
                                bgcolor: (theme) =>
                                  alpha(
                                    theme.palette.primary.main,
                                    isSelectAll ? 0.14 : 0.06
                                  ),
                              },
                            }}
                          >
                            <Checkbox
                              checked={checked}
                              indeterminate={indeterminate}
                              size="small"
                              sx={{ mr: 1 }}
                            />

                            <ListItemText
                              primary={option.name}
                              sx={{ flex: 1 }}
                              primaryTypographyProps={{
                                fontWeight: isSelectAll ? 700 : 500,
                                color: isSelectAll ? 'primary.main' : 'text.primary',
                                fontSize: '0.875rem',
                              }}
                            />
                            {isSelectAll && (
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, color: 'primary.main' }}
                              >
                                {currentValue.length}/{safeDepts.length}
                              </Typography>
                            )}
                          </Box>
                        );
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option.id}
                            label={option.name}
                            size="small"
                            onDelete={getTagProps({ index }).onDelete}
                            sx={{
                              borderRadius: 1,
                              fontWeight: 600,
                              m: 0.4,
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                              color: 'primary.dark',
                              '& .MuiChip-deleteIcon': {
                                color: 'primary.main',
                                '&:hover': { color: 'primary.dark' },
                              },
                            }}
                          />
                        ))
                      }
                      limitTags={3}
                      size={isSmallScreen ? 'small' : 'medium'}
                      noOptionsText={
                        <Stack alignItems="center" spacing={0.5} sx={{ py: 1 }}>
                          <Iconify
                            icon="eva:search-outline"
                            width={20}
                            sx={{ color: 'text.disabled' }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {t('formControl.noDepartmentFound', 'No matching department')}
                          </Typography>
                        </Stack>
                      }
                      ListboxProps={{ sx: { ...scrollbarStyle, py: 0.5 } }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('attributes.department')}
                          error={!!errors.departmentIds}
                          helperText={errors.departmentIds?.message}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <Iconify
                                  icon="eva:search-outline"
                                  width={18}
                                  sx={{ color: 'text.disabled', ml: 0.5, mr: 0.5 }}
                                />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      )}
                    />
                  );
                }}
              />
            )}

            <TextField
              name="email"
              label={t('pages.usersPage.form.emailAdress')}
              error={!!errors.email}
              helperText={errors.email?.message || ''}
              {...register('email', {
                required: t('formControl.enterEmail'),
                pattern: { value: EMAIL_REGEX, message: t('formControl.invalidEmail') },
              })}
              size={isSmallScreen ? 'small' : 'medium'}
            />
          </Stack>

          <Stack direction="row" spacing={2} sx={{ my: 3 }}>
            <LoadingButton
              fullWidth
              variant="contained"
              color="error"
              disabled={!isDirty}
              onClick={() => reset()}
            >
              {t('buttons.cancelChanges')}
            </LoadingButton>
            <LoadingButton
              fullWidth
              type="submit"
              variant="contained"
              color="success"
              disabled={!isValid || !isDirty}
            >
              {t('buttons.update')}
            </LoadingButton>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
}
